/**
 * DocumentsView (P276.2) -- the document locker at /admin/documents.
 *
 * Upload (loose files and/or .zip) -> staged-batch review (entries + dup
 * flags) -> commit (documents created + hash-chained + extraction queued
 * on the P144 substrate) -> inventory (extraction-status pills, manual-
 * text drawer for the OCR-deferred flag, delete).
 *
 * Admin console surface: plain English (the admin views stay outside the
 * i18n catalogue by convention).
 */
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useRef, useState } from "react";

import { MarsadIcon } from "shared/components/MarsadIcon";
import type { DocumentRow, IngestBatch } from "shared/api/types";
import {
  DocumentReadingNote,
  isHiddenLine,
} from "shared/documents/DocumentReadingNote";
import { hasPosition, useMe } from "shared/api/useMe";
import {
  useCommitBatch,
  useDeleteDocument,
  useDiscardBatch,
  useDocuments,
  useDocumentText,
  useExtractRuns,
  useSetManualText,
  useUploadDocuments,
} from "shared/documents/useDocuments";

const STATUS_PILL: Record<DocumentRow["extraction_status"], string> = {
  pending: "bg-slate-100 text-slate-600 border-slate-200",
  extracted: "bg-emerald-100 text-emerald-800 border-emerald-200",
  needs_manual_text: "bg-amber-100 text-amber-900 border-amber-200",
  failed: "bg-rose-100 text-rose-800 border-rose-200",
};

function formatBytes(n: number): string {
  if (n >= 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  if (n >= 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${n} B`;
}

function UploadPanel({ onStaged }: { onStaged: (b: IngestBatch) => void }) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const upload = useUploadDocuments();

  const onUpload = () => {
    const files = Array.from(inputRef.current?.files ?? []);
    if (files.length === 0) return;
    upload.mutate(files, {
      onSuccess: (batch) => {
        onStaged(batch);
        if (inputRef.current) inputRef.current.value = "";
      },
    });
  };

  return (
    <section
      data-testid="documents-upload"
      className="rounded-lg border border-slate-200 bg-white p-4 space-y-3"
    >
      <h2 className="text-sm font-semibold text-slate-800">
        {t("screens.documentsView.uploadDocuments")}
      </h2>
      <p className="text-xs text-slate-500 max-w-prose">
        {t("screens.documentsView.looseFilesOrZipArchivesPdfDocx")}
      </p>
      <div className="flex flex-wrap items-center gap-3">
        <input
          ref={inputRef}
          data-testid="documents-file-input"
          type="file"
          multiple
          accept=".pdf,.docx,.doc,.xlsx,.txt,.md,.csv,.html,.json,.xml,.zip"
          className="text-xs"
        />
        <button
          type="button"
          data-testid="documents-upload-button"
          onClick={onUpload}
          disabled={upload.isPending}
          className="rounded bg-brand-brass px-3 py-1.5 text-xs font-semibold text-brand-navy hover:bg-brand-brassDark disabled:opacity-50"
        >
          {upload.isPending
            ? t("screens.documentsView.uploading")
            : t("screens.documentsView.uploadStage")}
        </button>
      </div>
      {upload.isError && (
        <p data-testid="documents-upload-error" className="text-xs text-rose-700">
          {upload.error.message}
        </p>
      )}
    </section>
  );
}

function StagedBatchPanel({
  batch,
  onDone,
}: {
  batch: IngestBatch;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const commit = useCommitBatch();
  const discard = useDiscardBatch();
  const [result, setResult] = useState<string | null>(null);

  return (
    <section
      data-testid="documents-staged-batch"
      className="rounded-lg border border-brand-brass/40 bg-brand-brass/5 p-4 space-y-3"
    >
      <h2 className="text-sm font-semibold text-slate-800">
        {t("screens.documentsView.stagedBatch")} {batch.total}{" "}
        {t("screens.documentsView.entries")}
        {batch.new_count} {t("screens.documentsView.new")} {batch.duplicate_count}{" "}
        {t("screens.documentsView.duplicate")}
      </h2>
      <div className="max-h-64 overflow-y-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-start text-[10px] uppercase tracking-wide text-slate-400">
              <th className="text-start py-1">{t("screens.documentsView.file")}</th>
              <th className="text-start py-1">{t("screens.documentsView.size")}</th>
              <th className="text-start py-1">{t("screens.documentsView.sha256")}</th>
              <th className="text-start py-1"></th>
            </tr>
          </thead>
          <tbody>
            {batch.entries.map((e, i) => (
              <tr key={`${e.sha256}-${i}`} className="border-t border-slate-100">
                <td className="py-1 pe-2">
                  {e.filename}
                  {e.source_path && (
                    <span className="ms-1 text-slate-400">({e.source_path})</span>
                  )}
                </td>
                <td className="py-1 pe-2 tabular-nums">{formatBytes(e.size_bytes)}</td>
                <td className="py-1 pe-2 font-mono text-slate-400">
                  {e.sha256.slice(0, 10)}…
                </td>
                <td className="py-1">
                  {(e.duplicate || e.duplicate_of) && (
                    <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-900">
                      {t("screens.documentsView.duplicateWillSkip")}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {result ? (
        /* A7 -- what a commit MEANS, and where it leads.
           This ended at "Committed: 1 documents created" and the row
           then sat at STATUS "pending" with an em dash for TEXT. The
           preparer had no way to know whether the file she had just
           committed could be read at all, when that would be true, or
           what it was for -- so the best-executed path of the round
           stopped at a table cell. */
        <div data-testid="documents-commit-result" className="space-y-1">
          <p className="text-xs text-emerald-800">{result}</p>
          <p className="text-xs text-slate-600">
            {t("screens.documentsView.extractionQueuedExplainer")}
          </p>
          <Link
            to="/framework/document-gap"
            data-testid="documents-commit-to-gap"
            className="inline-block text-xs font-semibold text-brand-brassDark hover:underline"
          >
            {t("screens.documentsView.backToDocumentGap")}
          </Link>
        </div>
      ) : (
        <div className="flex gap-2">
          <button
            type="button"
            data-testid="documents-commit-button"
            disabled={commit.isPending}
            onClick={() =>
              commit.mutate(batch.id, {
                onSuccess: (r) => {
                  setResult(
                    `Committed: ${r.created} documents created, ` +
                      `${r.duplicates_skipped} duplicates skipped. ` +
                      (r.extract_run_id ? "Extraction queued." : ""),
                  );
                  setTimeout(onDone, 4000);
                },
              })
            }
            className="rounded bg-brand-brass px-3 py-1.5 text-xs font-semibold text-brand-navy hover:bg-brand-brassDark disabled:opacity-50"
          >
            {commit.isPending
              ? t("screens.documentsView.committing")
              : t("screens.documentsView.commitBatch")}
          </button>
          <button
            type="button"
            data-testid="documents-discard-button"
            disabled={discard.isPending}
            onClick={() => discard.mutate(batch.id, { onSuccess: onDone })}
            className="rounded border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 hover:border-rose-300 hover:text-rose-700 disabled:opacity-50"
          >
            {t("screens.documentsView.discard")}
          </button>
        </div>
      )}
    </section>
  );
}

/**
 * Round 13 A3 -- read the extracted text.
 *
 * The acceptance criterion's second limb. The product held this text,
 * fetched it, hashed it and judged a bank's compliance position on it,
 * and rendered it as a character count. Read-only on purpose: replacing
 * it is a different act and lives in the manual-text drawer, where it
 * has always been.
 */
function ExtractedTextDrawer({
  doc,
  onClose,
}: {
  doc: DocumentRow;
  onClose: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const text = useDocumentText(doc.id);
  return (
    <div
      data-testid="documents-read-text-drawer"
      className="space-y-2 rounded border border-slate-300 bg-white p-3"
    >
      <h3 className="text-sm font-semibold text-slate-800">
        {doc.title || doc.filename}
      </h3>
      <p className="text-[11px] text-slate-500">
        {t("screens.documentsView.readTextNote")}
      </p>
      {text.isLoading && (
        <p className="text-xs text-slate-400">{t("screens.documentsView.loading")}</p>
      )}
      <DocumentReadingNote reading={doc} testId="documents-read-text-reading" />
      {/* Round 16 A26 -- the count said one was hidden and named none:
          the passages, each marked when the original did not show it. */}
      {(text.data?.directed_passages?.length ?? 0) > 0 && (
        <ul
          data-testid="documents-read-text-directed"
          className="space-y-1 text-[11px]"
        >
          {(text.data?.directed_passages ?? []).map((p, i) => {
            const hidden = (text.data?.hidden_directed_passages ?? []).includes(p);
            return (
              <li
                key={i}
                data-testid="documents-read-text-directed-passage"
                data-hidden={hidden ? "true" : "false"}
                dir="auto"
                className={hidden ? "bg-amber-50 text-amber-900" : "text-slate-700"}
              >
                <q>{p}</q>
                {hidden && (
                  <span className="ms-1 text-[10px] font-semibold">
                    ({t("documents.reading.notVisible")})
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {text.data?.text ? (
        <pre
          dir="auto"
          data-testid="documents-read-text-body"
          className="max-h-96 overflow-y-auto whitespace-pre-wrap rounded bg-slate-50 p-2 text-[11px] text-slate-700"
        >
          {/* A17 -- a line the original did not show is marked as
              such, so text nobody could see is not read as if a person
              had written it for a person. */}
          {text.data.text.split("\n").map((line, i) =>
            isHiddenLine(line, text.data?.hidden_passages ?? []) ? (
              <span
                key={i}
                data-testid="documents-read-text-hidden"
                className="block bg-amber-50 text-amber-900"
              >
                {line}{" "}
                <span className="text-[10px] font-semibold not-italic">
                  ({t("documents.reading.notVisible")})
                </span>
              </span>
            ) : (
              <span key={i} className="block">
                {line}
              </span>
            ),
          )}
        </pre>
      ) : (
        !text.isLoading && (
          <p data-testid="documents-read-text-empty" className="text-xs text-slate-500">
            {t("screens.documentsView.readTextEmpty")}
          </p>
        )
      )}
      <button
        type="button"
        data-testid="documents-read-text-close"
        onClick={onClose}
        className="rounded border border-slate-300 px-3 py-1.5 text-xs text-slate-700 hover:border-brand-brass"
      >
        {t("screens.documentsView.close")}
      </button>
    </div>
  );
}

function ManualTextDrawer({ doc, onClose }: { doc: DocumentRow; onClose: () => void }) {
  const { t } = useTranslation();
  const existing = useDocumentText(doc.id);
  const save = useSetManualText();
  const [text, setText] = useState("");

  return (
    <div
      data-testid="documents-manual-text-drawer"
      className="rounded-lg border border-amber-300 bg-amber-50 p-4 space-y-2"
    >
      <h3 className="text-sm font-semibold text-slate-800">
        {t("screens.documentsView.manualText")} {doc.filename}
      </h3>
      <p className="text-xs text-slate-600">
        {t("screens.documentsView.thisDocumentAposSTextCouldnApos")}
      </p>
      {existing.data?.text && (
        <p className="text-xs italic text-slate-500">
          {t("screens.documentsView.existingText")}
          {existing.data.extractor}): {existing.data.text.length}{" "}
          {t("screens.documentsView.charsSavingReplacesIt")}
        </p>
      )}
      <textarea
        dir="auto"
        data-testid="documents-manual-text-input"
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={8}
        className="w-full rounded border border-slate-300 p-2 text-xs"
      />
      <div className="flex gap-2">
        <button
          type="button"
          data-testid="documents-manual-text-save"
          disabled={save.isPending || !text.trim()}
          onClick={() => save.mutate({ id: doc.id, text }, { onSuccess: onClose })}
          className="rounded bg-brand-brass px-3 py-1.5 text-xs font-semibold text-brand-navy hover:bg-brand-brassDark disabled:opacity-50"
        >
          {t("screens.documentsView.saveText")}
        </button>
        <button
          type="button"
          onClick={onClose}
          className="rounded border border-slate-300 bg-white px-3 py-1.5 text-xs text-slate-600"
        >
          {t("screens.documentsView.close")}
        </button>
      </div>
    </div>
  );
}

export function DocumentsView() {
  const { t } = useTranslation();
  const me = useMe();
  const runs = useExtractRuns();
  const extracting =
    runs.data?.some((r) => r.status === "queued" || r.status === "running") ?? false;
  // A7 -- the inventory refreshes itself while extraction is in flight,
  // so a preparer watching her own commit sees "pending" become
  // "extracted" rather than having to guess when to reload.
  const docs = useDocuments(extracting);
  const del = useDeleteDocument();
  const [staged, setStaged] = useState<IngestBatch | null>(null);
  const [manualDoc, setManualDoc] = useState<DocumentRow | null>(null);
  // Round 13 A3 -- which document is open for reading.
  const [readDoc, setReadDoc] = useState<DocumentRow | null>(null);

  // C1 -- reading the inventory is not an administrative act, and a
  // preparer asked for a Test of Design has to see the policy the test
  // is about. Uploading, committing and discarding change the estate
  // and stay administrative; the list, and the mapping decisions on
  // it, are the compliance work.
  // D9 -- the preparer holds the bank's policy documents and needs them
  // in the locker before Test of Design will run at all. Mirrors the
  // gate, which admits admin.
  const canIngest = (me.data?.is_admin ?? false) || hasPosition(me.data, "preparer");
  const canDelete = me.data?.is_admin ?? false;
  if (me.data && !canIngest && !hasPosition(me.data, "preparer")) {
    return (
      <p data-testid="documents-forbidden" className="text-sm text-rose-700 p-6">
        {t("screens.documentsView.adminRoleRequired")}
      </p>
    );
  }

  const activeRun = runs.data?.find(
    (r) => r.status === "running" || r.status === "queued",
  );
  const rows = docs.data?.documents ?? [];

  return (
    <div data-testid="documents-view" className="mx-auto max-w-5xl space-y-5 px-4 py-6">
      <header>
        <p className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-slate-400">
          {t("screens.documentsView.documentLocker")}
        </p>
        <h1 className="font-display text-2xl text-slate-900">
          <MarsadIcon
            name="document-locker"
            size={20}
            className="me-2 inline-block align-[-0.1em] text-brand-brassDark"
          />
          {t("screens.documentsView.documents")}
        </h1>
        <p className="mt-1 max-w-prose text-sm text-slate-500">
          {t("screens.documentsView.provablyHashedProcessingCopiesOfTheBank")}
        </p>
      </header>

      {/* C1 -- ingest changes the estate, so it stays administrative.
          The inventory beneath it does not. */}
      {canIngest && <UploadPanel onStaged={setStaged} />}
      {canIngest && staged && (
        <StagedBatchPanel batch={staged} onDone={() => setStaged(null)} />
      )}

      {activeRun && (
        <p data-testid="documents-extract-progress" className="text-xs text-slate-600">
          {t("screens.documentsView.extraction")} {activeRun.status}:{" "}
          {activeRun.processed}
          {activeRun.total != null ? ` of ${activeRun.total}` : ""}{" "}
          {t("screens.documentsView.documents2")}
        </p>
      )}

      {manualDoc && (
        <ManualTextDrawer doc={manualDoc} onClose={() => setManualDoc(null)} />
      )}
      {readDoc && (
        <ExtractedTextDrawer doc={readDoc} onClose={() => setReadDoc(null)} />
      )}

      <section data-testid="documents-inventory" className="space-y-2">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          {t("screens.documentsView.inventory")}
          {docs.data?.total ?? 0})
        </h2>
        {docs.isLoading && (
          <p className="text-sm text-slate-400">{t("screens.documentsView.loading")}</p>
        )}
        {rows.length === 0 && !docs.isLoading && (
          <p data-testid="documents-empty" className="text-sm text-slate-500">
            {t("screens.documentsView.noDocumentsYetUploadTheBankApos")}
          </p>
        )}
        {rows.length > 0 && (
          <table className="w-full text-xs">
            <thead>
              <tr className="text-[10px] uppercase tracking-wide text-slate-400">
                <th className="text-start py-1">{t("screens.documentsView.file")}</th>
                <th className="text-start py-1">{t("screens.documentsView.size")}</th>
                <th className="text-start py-1">{t("screens.documentsView.status")}</th>
                <th className="text-start py-1">{t("screens.documentsView.text")}</th>
                <th className="text-start py-1">{t("screens.documentsView.sha256")}</th>
                <th className="text-start py-1"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((d) => (
                <tr
                  key={d.id}
                  data-testid={`document-row-${d.id}`}
                  className="border-t border-slate-100"
                >
                  <td className="py-1.5 pe-2 font-medium text-slate-800">
                    {d.filename}
                    <DocumentReadingNote
                      reading={d}
                      testId={`document-reading-${d.id}`}
                    />
                  </td>
                  <td className="py-1.5 pe-2 tabular-nums text-slate-500">
                    {formatBytes(d.size_bytes)}
                  </td>
                  <td className="py-1.5 pe-2">
                    <span
                      className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${STATUS_PILL[d.extraction_status]}`}
                    >
                      {d.extraction_status.replace(/_/g, " ")}
                    </span>
                  </td>
                  <td className="py-1.5 pe-2 tabular-nums text-slate-500">
                    {/* Round 9 B4. An em dash where the text figure goes,
                        beside a pill reading "pending", told the preparer
                        nothing about what she was waiting for or whether
                        waiting was even the right thing to do. */}
                    {d.extraction_status === "pending" ? (
                      <span
                        data-testid={`document-pending-${d.id}`}
                        className="not-italic text-slate-600"
                      >
                        {t("screens.documentsView.awaitingExtraction")}
                      </span>
                    ) : (
                      <>
                        {d.char_count != null ? `${d.char_count} ch` : "—"}
                        {d.page_count != null ? ` · ${d.page_count} pp` : ""}
                      </>
                    )}
                  </td>
                  <td className="py-1.5 pe-2 font-mono text-slate-400">
                    {d.sha256.slice(0, 10)}…
                  </td>
                  <td className="py-1.5 space-x-2 whitespace-nowrap">
                    {/* ROUND 13 A3 -- the extracted text could not be read
                        anywhere in the product: the one component that
                        fetched it rendered its LENGTH. A reviewer asking
                        what a compliance judgement rested on could reach a
                        filename, a character count and a hash. */}
                    {d.char_count != null && d.char_count > 0 && (
                      <button
                        type="button"
                        data-testid={`document-read-${d.id}`}
                        onClick={() => setReadDoc(d)}
                        className="text-brand-brassDark hover:underline"
                      >
                        {t("screens.documentsView.readText")}
                      </button>
                    )}
                    {d.extraction_status ===
                      t("screens.documentsView.needsManualText") && (
                      <button
                        type="button"
                        data-testid={`document-manual-${d.id}`}
                        onClick={() => setManualDoc(d)}
                        className="text-amber-800 hover:underline"
                      >
                        {t("screens.documentsView.addText")}
                      </button>
                    )}
                    {/* C1 -- removing a document from the estate is
                        administrative; reading it is not.
                        D9 -- and it does NOT ride canIngest any more.
                        Widening who may add to the locker would
                        otherwise have widened who may take evidence
                        out from under a recorded judgement, which is a
                        different act with a different consequence. */}
                    {canDelete && (
                      <button
                        type="button"
                        data-testid={`document-delete-${d.id}`}
                        onClick={() => del.mutate(d.id)}
                        className="text-slate-400 hover:text-rose-700 hover:underline"
                      >
                        {t("screens.documentsView.delete")}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
